import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { Tag } from 'primeng/tag';
import type { SnapshotStatus } from '../financial-snapshot.model';

@Component({
  selector: 'bn-financial-snapshot-state-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Tag],
  template: `
    @if (status(); as s) {
      <p-tag
        [value]="label()"
        [severity]="severity()"
        [icon]="icon()"
        [rounded]="true"
        styleClass="bn-fs-badge"
      />
    }
  `,
})
export class FinancialSnapshotStateBadgeComponent {
  readonly status = input.required<SnapshotStatus>();
  /** ISO date for “Last updated …” copy in the badge. */
  readonly updatedAt = input<string | null>(null);

  label(): string {
    const date = this.updatedAt();
    const datePart = date ? `Last updated ${this._formatDate(date)}` : null;
    switch (this.status()) {
      case 'empty':
        return 'Not started';
      case 'in_progress':
        return datePart ? `In progress · ${datePart}` : 'In progress';
      case 'complete':
        return datePart ?? 'Complete';
      case 'outdated':
        return datePart ? `Outdated · ${datePart}` : 'Outdated';
    }
  }

  severity(): 'secondary' | 'info' | 'success' | 'warn' {
    switch (this.status()) {
      case 'empty':
        return 'secondary';
      case 'in_progress':
        return 'info';
      case 'complete':
        return 'info';
      case 'outdated':
        return 'warn';
    }
  }

  icon(): string {
    switch (this.status()) {
      case 'empty':
        return 'pi pi-minus-circle';
      case 'in_progress':
        return 'pi pi-clock';
      case 'complete':
        return 'pi pi-check-circle';
      case 'outdated':
        return 'pi pi-exclamation-triangle';
    }
  }

  private _formatDate(iso: string): string {
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date(iso));
    } catch {
      return '';
    }
  }
}
